import { Link } from 'react-router-dom';
import { ArrowUpDown, MoreHorizontal, Trash2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { parentStudentStatus } from '~/pages/ParentEnrollment/parentStudentStatus';
import { Badge, type BadgeProps } from '~/components/ui/badge';
import { createSelectColumn } from '~/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import type { StudentEnrollmentRow } from './types';

const stateColors: Record<string, string> = {
  PENDING_FAMILY_CONFIRMATION: 'secondary',
  PENDING_ADMISSIONS_REVIEW: 'outline',
  WAITLISTED: 'destructive',
  ACCEPTED_PENDING_DEPOSIT: 'default',
  ENROLLED: 'default',
  WITHDRAWN: 'destructive',
  DENIED: 'destructive',
};

const kindLabels: Record<string, string> = {
  NEW_STUDENT: 'New',
  RETURNING_STUDENT: 'Returning',
};

export const getStudentColumns = (schoolSlug: string | undefined, onDelete?: (enrollment: StudentEnrollmentRow) => void): ColumnDef<StudentEnrollmentRow>[] => [
  createSelectColumn<StudentEnrollmentRow>(),
  {
    id: 'studentName',
    accessorFn: (row) => `${row.person?.firstName} ${row.person?.lastName}`,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Student
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const person = row.original.person;
      return (
        <Link
          to={`/app/${schoolSlug}/students/${person?.id}/enrollment`}
          className="font-medium hover:underline"
        >
          {person?.firstName} {person?.lastName}
        </Link>
      );
    },
  },
  {
    id: 'familyName',
    accessorFn: (row) => row.familyEnrollment?.family?.name,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Family
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const family = row.original.familyEnrollment?.family;
      return (
        <Link
          to={`/app/${schoolSlug}/families/${family?.slug}/enrollment`}
          className="hover:underline"
        >
          {family?.name}
        </Link>
      );
    },
  },
  {
    accessorKey: 'enrollmentKind',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant="outline">
        {kindLabels[row.original.enrollmentKind] || row.original.enrollmentKind}
      </Badge>
    ),
    meta: {
      facet: {
        title: 'Type',
        getKey: (row: StudentEnrollmentRow) => row.enrollmentKind,
        getLabel: (row: StudentEnrollmentRow) => kindLabels[row.enrollmentKind] || row.enrollmentKind,
      },
    },
    filterFn: (row, id, value) => {
      return (value as string[]).includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'state',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={(stateColors[row.original.state] || 'secondary') as BadgeProps["variant"]}>
        {parentStudentStatus(row.original.state)}
      </Badge>
    ),
    meta: {
      facet: {
        title: 'Status',
        getKey: (row: StudentEnrollmentRow) => row.state,
        getLabel: (row: StudentEnrollmentRow) => parentStudentStatus(row.state),
      },
    },
    filterFn: (row, id, value) => {
      return (value as string[]).includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'gradeLevel',
    header: 'Grade',
    cell: ({ row }) => (
      <div>{row.original.gradeLevel || '-'}</div>
    ),
    meta: {
      facet: {
        title: 'Grade',
        getKey: (row: StudentEnrollmentRow) => row.gradeLevel,
        getLabel: (row: StudentEnrollmentRow) => row.gradeLevel,
      },
    },
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Last Updated
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.updatedAt as string);
      return <div>{date.toLocaleDateString()}</div>;
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const enrollment = row.original;
      const person = enrollment.person;
      const family = enrollment.familyEnrollment?.family;
      const canDelete = enrollment.familyEnrollment?.access === 'NOT_INVITED';

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(enrollment.id)}
            >
              Copy ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={`/app/${schoolSlug}/students/${person?.id}/enrollment`}>
                View Enrollment
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/app/${schoolSlug}/students/${person?.id}`}>
                View Student
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/app/${schoolSlug}/families/${family?.slug}`}>
                View Family
              </Link>
            </DropdownMenuItem>
            {canDelete && onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(enrollment)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove from Year
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
